import { Component, OnInit } from '@angular/core';
import {
    LifecycleAware,
    AuthenticationService,
    Modal,
    ModalService
} from '@lsq-frontend/common-ui';
import { MfaBackupCodesService } from '@lsq-frontend/fasttrack-shared/features/mfa-backup-codes';
import { BehaviorSubject } from 'rxjs';
import { TwoFactorService } from './two-factor.service';

@LifecycleAware()
@Component({
    selector: 'two-factor',
    template: `
        <div class="two-factor-section">
            <div class="two-factor-header section-header">
                <div>
                    <h2 class="section-heading h3">
                        <ng-container i18n>Multi-Factor Authentication</ng-container>
                    </h2>
                    <div class="section-subheading" i18n>
                        Keep your account extra secure with a second authentication step
                    </div>
                </div>
            </div>

            <div class="user-info">
                <div class="user-info-display">
                    <div class="user-info-display-group" [style.border-bottom]="'none'">
                        <div id="two-factor-mobile-phone-number" class="user-info-display-pair">
                            <div class="user-info-display-pair-label">
                                <b i18n>Authentication Method</b>
                            </div>
                            <div class="user-info-display-pair-value">
                                <ng-container
                                    *ngIf="
                                        twoFactorService.preferredMfaMethodSubject.value ==
                                        'SOFTWARE_TOKEN_MFA'
                                    "
                                >
                                    Duo Mobile
                                </ng-container>
                                <ng-container
                                    *ngIf="
                                        twoFactorService.preferredMfaMethodSubject.value !=
                                        'SOFTWARE_TOKEN_MFA'
                                    "
                                >
                                    SMS<br />
                                    {{ twoFactorService.userPhoneNumber$ | async | phone }}
                                </ng-container>
                                <div>
                                    <lsq-button
                                        id="edit-button"
                                        appearance="transparent"
                                        (buttonClick)="enrollMFA()"
                                        i18n="
                                            Button to edit the phone number for multi-factor
                                            verification
                                        "
                                    >
                                        Enroll New Device
                                    </lsq-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="backup-codes-section">
            <div class="backup-codes section-header">
                <div>
                    <h2 class="section-heading h3">
                        <ng-container i18n>Backup Codes</ng-container>
                    </h2>
                    <div class="section-subheading" i18n>
                        Backup codes enable you to sign in if you do not have your device with you.
                    </div>
                </div>
            </div>
            <div class="user-info">
                <div class="user-info-display">
                    <div class="user-info-display-group" [style.border-bottom]="'none'">
                        <lsq-button
                            id="generate-code-button"
                            [style.margin-top]="'.5rem'"
                            [style.margin-bottom]="'.5rem'"
                            appearance="secondary"
                            (buttonClick)="generateBackupCodes()"
                            i18n
                        >
                            Generate New Backup Codes
                        </lsq-button>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class TwoFactorComponent implements OnInit {
    public preferredMfaMethod: string;

    constructor(
        public twoFactorService: TwoFactorService,
        private _modalService: ModalService,
        private _mfaBackupCodesService: MfaBackupCodesService,
        public authenticationService: AuthenticationService
    ) {
        this.twoFactorService.getPreferredMethod();
    }

    public async ngOnInit(): Promise<void> {}

    public enrollMFA(): void {
        this.twoFactorService.enrollMFA(this.preferredMfaMethod);
    }

    public generateBackupCodes(): void {
        const submittingSubject = new BehaviorSubject<boolean>(false);
        this._modalService.showModal(
            new Modal({
                title: 'Generate New Backup Codes',
                bodyText:
                    'Completing this action will generate new backup codes and will invalidate any current remaining backup codes that have not been used. Do you wish to continue?',
                showFooter: true,
                showCancelButton: true,
                showCloseButton: true,
                showOkButton: true,
                okButtonText: 'Confirm',
                okButtonSize: 'block',
                okButtonIsDisabled$: submittingSubject.asObservable(),
                onOkClick: async () => {
                    submittingSubject.next(true);
                    await this._mfaBackupCodesService.showNewMfaBackupCodesModal(false);
                }
            })
        );
    }
}
